import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { EyeOutlined, PlayCircleOutlined } from '@ant-design/icons';
import ImageSlide, { ImageSlideProps } from './ImageSlide';
import { usePreviewModalContext } from './PreviewModal';
import StatusImage from './StatusImage';
import ProxyVideo from 'components/ProxyVideo';
import { useCallback } from 'react';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  cursor: pointer;
  > div {
    height: 100%;
  }
  > section {
    display: none;
  }
  &:hover {
    > section {
      display: flex;
    }
  }
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayIcon = styled(PlayCircleOutlined)`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 40px;
  display: block;
  color: #fff;
  background: #aaa;
  border-radius: 100%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const Preview = styled.section`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.2);
  z-index: 2;
`;

export interface SiderImageSlideProps extends ImageSlideProps {
  width?: number;
  extra?: {
    platform: General.PlatformUpperCaseTunnel;
    region: General.UpperCaseRegion;
    storeId: string;
    tradeId: string;
  };
}

function SiderImageSlide(props: SiderImageSlideProps) {
  const { images, videos, width, extra, ...otherProps } = props;

  const { t } = useTranslate();

  const { show } = usePreviewModalContext();

  const handleShow = useCallback(
    (index: number) => {
      show({
        images,
        videos,
        width,
        initialIndex: index,
        extra,
      });
    },
    [show, images, videos, width, extra],
  );

  return (
    <ImageSlide
      images={images}
      videos={videos}
      platform={extra?.platform as General.PlatformUpperCase}
      imageRender={useCallback(
        (src, props, index) => {
          return (
            <Wrapper
              onClick={(e) => {
                handleShow(index + (videos || []).length);
                e.stopPropagation();
              }}
            >
              <StatusImage src={src} platform={extra?.platform} {...props} />
              <Preview>
                <EyeOutlined />
                {t('scene.chatWorkspace.orderTab.preview.action.preview')}
              </Preview>
            </Wrapper>
          );
        },
        [handleShow, t, videos, extra?.platform],
      )}
      videoRender={useCallback(
        (src, index) => {
          return (
            <Wrapper
              onClick={(e) => {
                handleShow(index);
                e.stopPropagation();
              }}
            >
              <ProxyVideo src={src}>
                <track kind="captions" />
              </ProxyVideo>
              <PlayIcon />
              <Preview>
                <EyeOutlined />
                {t('scene.chatWorkspace.orderTab.preview.action.preview')}
              </Preview>
            </Wrapper>
          );
        },
        [handleShow, t],
      )}
      {...otherProps}
    />
  );
}

export default SiderImageSlide;
